/**
 * Same 3D model released onto more than one structure-group lane.
 * Identity is `sourceStructureReleaseId` (the upload's structureReleaseId), not file name or size.
 */

export interface SharedModelRelease {
  id: string;
  dealId: string;
  streamKind: 'two_d_pdf' | 'three_d_html';
  designPhase?: 'concept' | 'engineering';
  sourceStructureReleaseId?: string;
  sourceStreamId?: string;
  supersededByReleaseId?: string;
  approvalRequested?: boolean;
  approved?: boolean;
  bundleId?: string;
  releaseVersionIndex?: number;
}

export interface SharedModelBundle {
  id: string;
  releaseIds: string[];
}

export interface SharedModelStamp {
  releaseId: string;
  bundleId?: string;
  releaseVersionIndex?: number;
  /** True once per bundle, and only when every member of that bundle is being approved. */
  stampBundle: boolean;
}

export interface SharedModelGroup {
  key: string;
  /** Structure-group labels this signature will still approve. */
  pendingLabels: string[];
  /** Structure-group labels already approved for this same model. */
  approvedLabels: string[];
}

function phaseOf(release: SharedModelRelease): 'concept' | 'engineering' {
  return release.designPhase === 'engineering' ? 'engineering' : 'concept';
}

function bundleFor(release: SharedModelRelease, bundles: SharedModelBundle[]): SharedModelBundle | undefined {
  if (release.bundleId) {
    const direct = bundles.find(bundle => bundle.id === release.bundleId);
    if (direct) return direct;
  }
  return bundles.find(bundle => bundle.releaseIds.includes(release.id));
}

/** Current on its own lane: not superseded, and no newer release of the same track and phase. */
export function isCurrentSharedModelRelease(
  release: SharedModelRelease,
  releases: SharedModelRelease[],
): boolean {
  if (release.supersededByReleaseId) return false;
  const phase = phaseOf(release);
  const streamId = release.sourceStreamId || '';
  return !releases.some(other => {
    if (other.id === release.id) return false;
    if ((other.sourceStreamId || '') !== streamId) return false;
    if (other.dealId !== release.dealId) return false;
    if (other.streamKind !== release.streamKind) return false;
    if (phaseOf(other) !== phase) return false;
    if (other.supersededByReleaseId) return false;
    return (other.releaseVersionIndex ?? 0) > (release.releaseVersionIndex ?? 0);
  });
}

/**
 * Releases and bundles one signature should stamp.
 * The release the client signed is always included, plus the other files in its bundle.
 * Sibling 3D copies are added only when they share `sourceStructureReleaseId`, phase, and
 * are still awaiting approval. A bundle that mixes this model with other files is not stamped.
 */
export function sharedModelApprovalStamps(
  signedReleaseId: string,
  releases: SharedModelRelease[],
  bundles: SharedModelBundle[],
): SharedModelStamp[] {
  const signed = releases.find(release => release.id === signedReleaseId);
  if (!signed) return [];

  const selected = new Map<string, SharedModelRelease>();
  selected.set(signed.id, signed);
  const signedBundle = bundleFor(signed, bundles);
  if (signedBundle) {
    for (const id of signedBundle.releaseIds) {
      const member = releases.find(release => release.id === id);
      if (member) selected.set(member.id, member);
    }
  }

  const key = (signed.sourceStructureReleaseId || '').trim();
  if (key && signed.streamKind === 'three_d_html' && isCurrentSharedModelRelease(signed, releases)) {
    const phase = phaseOf(signed);
    const candidates = releases.filter(release => {
      if (selected.has(release.id)) return false;
      if (release.dealId !== signed.dealId) return false;
      if (release.streamKind !== 'three_d_html') return false;
      if ((release.sourceStructureReleaseId || '').trim() !== key) return false;
      if (phaseOf(release) !== phase) return false;
      if (!isCurrentSharedModelRelease(release, releases)) return false;
      if (release.approvalRequested !== true) return false;
      if (release.approved) return false;
      return true;
    });
    const eligible = new Set<string>(Array.from(selected.keys()).concat(candidates.map(release => release.id)));
    for (const candidate of candidates) {
      const bundle = bundleFor(candidate, bundles);
      if (bundle && bundle.releaseIds.length > 1 && !bundle.releaseIds.every(id => eligible.has(id))) {
        continue;
      }
      selected.set(candidate.id, candidate);
    }
  }

  const stampedBundles = new Set<string>();
  const stamps: SharedModelStamp[] = [];
  for (const release of Array.from(selected.values())) {
    const bundle = bundleFor(release, bundles);
    let stampBundle = false;
    if (bundle && !stampedBundles.has(bundle.id) && bundle.releaseIds.every(id => selected.has(id))) {
      stampBundle = true;
      stampedBundles.add(bundle.id);
    }
    stamps.push({
      releaseId: release.id,
      bundleId: bundle?.id || release.bundleId,
      releaseVersionIndex: release.releaseVersionIndex,
      stampBundle,
    });
  }
  return stamps;
}

function uniqueLabels(labels: string[]): string[] {
  const out: string[] = [];
  for (const label of labels) {
    const trimmed = label.trim();
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  }
  return out;
}

function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/** Badge copy for the approval dialog and the 3D viewer. Null when there is only one lane. */
export function formatSharedModelApprovalNote(group: SharedModelGroup | null | undefined): string | null {
  if (!group) return null;
  const pending = uniqueLabels(group.pendingLabels);
  const approved = uniqueLabels(group.approvedLabels);
  const all = uniqueLabels([...pending, ...approved]);
  if (all.length < 2) return null;
  const list = joinLabels(all);
  if (approved.length === 0) {
    const scope = pending.length === 2 ? 'both structure groups' : 'all of these structure groups';
    return `You're reviewing the same model for ${list}. One approval here will approve ${scope}.`;
  }
  const approvedList = joinLabels(approved);
  const approvedVerb = approved.length === 1 ? 'is' : 'are';
  if (pending.length === 0) {
    return `You're reviewing the same model for ${list}. ${approvedList} ${approvedVerb} already approved.`;
  }
  return `You're reviewing the same model for ${list}. ${approvedList} ${approvedVerb} already approved. One approval here will approve ${joinLabels(pending)}.`;
}
